<template>
  <div class="booking-calendar">
    <button
      class="toggle-button"
      type="button"
      @click="toggleDatePicker()"
    >
      {{ selectedMonth }}
    </button>
    <DatePicker
      class="booking-datepicker"
      :is-visible="isDatePickerVisible"
      @select="handleDateSelect"
    />
    <DatePickerModal
      :is-visible="isDatePickerModalVisible"
      @close="toggleDatePicker"
      @select="handleDateSelect"
    />
    <div class="booking-slots-details">
      <button
        class="prev nav-button"
        type="button"
        :title="translationsStore.t('booking_calendar_previous')"
        :disabled="selectedDate <= new Date()"
        @click="goToPreviousSlots"
      >
        <img
          class="slot-icon"
          src="../assets/icons/prev.svg"
          alt=""
        />
      </button>
      <button
        class="next nav-button"
        type="button"
        :title="translationsStore.t('booking_calendar_next')"
        @click="goToNextSlots"
      >
        <img
          class="slot-icon"
          src="../assets/icons/next.svg"
          alt=""
        />
      </button>
      <n-radio-group
        v-if="!noMatch"
        v-model:value="form.slot"
        name="radiobuttongroup-slot"
        class="booking-days"
      >
        <div
          v-for="(day, index) in zenotiAvailabilities"
          :key="index"
          class="booking-day"
        >
          <div class="booking-day-header">
            <p class="booking-day-date">
              {{
                new Date(day.date).toLocaleString(locale, {
                  weekday: "short",
                })
              }}
              <span>{{ day.date.split("-")[2] }}</span>
            </p>
          </div>
          <div
            v-if="!loading"
            class="booking-slots"
          >
            <n-radio-button
              v-for="(slot, slotIndex) in getVisibleSlots(day)"
              :key="slotIndex"
              :value="slot"
              :label="slot.split('T')[1].substring(0, 5)"
            />
            <button
              v-if="hasMoreSlots(day)"
              type="button"
              class="show-more-button"
              @click="showMoreSlots(day)"
            >
              {{ translationsStore.t("see_more") }}
            </button>
          </div>
        </div>
      </n-radio-group>
      <p
        v-if="nextAvailableDate && !loading"
        class="next-available-date"
      >
        <span>{{ translationsStore.t("next_availability") }} : </span>
        <button
          type="button"
          @click="goToNextAvailabilities"
        >
          {{
            new Date(nextAvailableDate).toLocaleDateString("fr-FR", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })
          }}
        </button>
      </p>
      <p
        v-if="loading"
        class="message"
      >
        <SvgLoader />
      </p>
      <p
        v-if="noData && !noMatch"
        class="message"
      >
        {{ translationsStore.t("booking_slots_no_data") }}
      </p>
      <p
        v-if="!nextAvailableDate && !loading && noSlot && !noMatch"
        class="message"
      >
        {{ translationsStore.t("booking_slots_empty") }}
      </p>
      <p
        v-if="(noMatch || isNotMatched) && !loading"
        :class="`message no-match ${showNoMatch ? 'active' : ''}`"
      >
        {{ translationsStore.t("booking_slots_no_match") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from "vue";
import axios from "axios";
import { useBookingStore } from "../stores/booking";
import { formatDateToISO } from "../utils/formatDate";
import { useViewport } from "../composables/useViewport";
import DatePicker from "./molecules/DatePicker.vue";
import DatePickerModal from "./DatePickerModal.vue";
import { useTranslationsStore } from "../stores/translations";
import { idCenter } from "../utils/idCenter";
import SvgLoader from "./molecules/SvgLoader.vue";

const props = defineProps({
  noMatch: {
    type: Boolean,
    default: false,
  },
});

const translationsStore = useTranslationsStore();

const { isDesktop } = useViewport();
const bookingStore = useBookingStore();
const loading = ref(true);
const error = ref(null);
const noData = ref(false);
const selectedMonth = ref(null);
const selectedDate = ref(new Date());
const isDatePickerVisible = ref(false);
const isDatePickerModalVisible = ref(false);
const noSlot = ref(false);
const isNotMatched = ref(false);
const showNoMatch = ref(false);
const zenotiAvailabilities = ref([]);
const nextAvailableDate = ref(null);
const form = ref({
  slot: "",
});
const slotsVisibility = ref({});
const SLOTS_PER_PAGE = 8;
const locale = window.shopLocale || "en";
const { environment } = window.themeVariables;

const toggleDatePicker = () => {
  if (!isDesktop.value) {
    isDatePickerModalVisible.value = !isDatePickerModalVisible.value;
  } else {
    isDatePickerVisible.value = !isDatePickerVisible.value;
  }
};

const handleDateSelect = (date) => {
  toggleDatePicker();
  selectedDate.value = new Date(date);
  selectedMonth.value = new Date(date).toLocaleString(locale, {
    month: "long",
    year: "numeric",
  });
  getZenotiAvailabilities(date);
};

const goToPreviousSlots = () => {
  if (zenotiAvailabilities.value.length === 0) return;
  const returnValue = isDesktop.value ? 4 : 3;
  const firstDate = zenotiAvailabilities.value[0].date;
  selectedDate.value = new Date(firstDate);
  selectedDate.value.setDate(selectedDate.value.getDate() - returnValue);
  if (selectedDate.value < new Date()) {
    selectedDate.value = new Date();
  }
  selectedMonth.value = new Date(selectedDate.value).toLocaleString(locale, {
    month: "long",
    year: "numeric",
  });
  getZenotiAvailabilities(selectedDate.value);
};

const goToNextSlots = () => {
  if (zenotiAvailabilities.value.length === 0) return;
  const lastDate = zenotiAvailabilities.value.at(-1).date;
  selectedDate.value = new Date(lastDate);
  selectedDate.value.setDate(selectedDate.value.getDate() + 1);
  selectedMonth.value = new Date(selectedDate.value).toLocaleString(locale, {
    month: "long",
    year: "numeric",
  });
  getZenotiAvailabilities(selectedDate.value);
};

const goToNextAvailabilities = () => {
  if (nextAvailableDate.value) {
    selectedDate.value = new Date(nextAvailableDate.value);
    selectedMonth.value = new Date(selectedDate.value).toLocaleString(locale, {
      month: "long",
      year: "numeric",
    });
    getZenotiAvailabilities(selectedDate.value);
  }
};

const getVisibleSlots = (day) => {
  const visibleCount = slotsVisibility.value[day.date] || SLOTS_PER_PAGE;
  return day.slots.slice(0, visibleCount);
};

const hasMoreSlots = (day) => {
  const visibleCount = slotsVisibility.value[day.date] || SLOTS_PER_PAGE;
  return day.slots.length > visibleCount;
};

const showMoreSlots = (day) => {
  const currentVisible = slotsVisibility.value[day.date] || SLOTS_PER_PAGE;
  slotsVisibility.value[day.date] = Math.min(
    currentVisible + SLOTS_PER_PAGE,
    day.slots.length
  );
};

const getZenotiAvailabilities = async (date) => {
  try {
    loading.value = true;
    console.log(
      "bookingStore.zenotiService in getZenotiAVAilabilities = ",
      bookingStore.zenotiService
    );
    const response = await axios.post(
      `${environment.variables.bff_url}/pms/booking/availabilities`,
      {
        centerId: idCenter(bookingStore.currentCenter.id_center),
        serviceId: bookingStore.zenotiService?.id,
        date: formatDateToISO(date),
        therapistGender: "ANY",
      },
      {
        headers: {
          "shopify-edge-metadata-shop-id":
            environment.variables.shopify_shop_id,
          "ngrok-skip-browser-warning":
            environment.variables.ngrok_skip_warning,
        },
      }
    );

    nextAvailableDate.value = null;
    slotsVisibility.value = {};

    if (!response.data) {
      console.error("No data received");
      noData.value = true;
    }

    if (!isDesktop.value) {
      zenotiAvailabilities.value = response.data.slice(0, 3);
    } else {
      zenotiAvailabilities.value = response.data;
    }

    const totalDays = isDesktop.value ? 4 : 3;
    let daysWithoutSlot = 0;
    zenotiAvailabilities.value.forEach((day) => {
      if (day.slots.length === 0) {
        daysWithoutSlot++;
      }
    });
    if (daysWithoutSlot === totalDays) {
      if (
        response.data?.[0].futureDays?.length > 0 &&
        new Date(response.data[0].futureDays[0]) >
          new Date(response.data?.[0].date)
      ) {
        nextAvailableDate.value = response.data[0].futureDays[0];
        noSlot.value = false;
      } else {
        nextAvailableDate.value = null;
        noSlot.value = true;
      }
    } else {
      noSlot.value = false;
    }
  } catch (err) {
    error.value = "Failed to fetch data";
    loading.value = false;
    noData.value = true;
    console.error(err);
    if (err.status === 404) {
      isNotMatched.value = true;
    }
  } finally {
    loading.value = false;
    // noData.value = false;
  }
};

watch(
  () => form.value.slot,
  () => {
    bookingStore.updateCurrentBookingSlot({
      slot: form.value.slot,
      centerId: idCenter(bookingStore.currentCenter.id_center),
      serviceId: bookingStore.zenotiService?.code,
    });
  }
);

watch(
  () => bookingStore.zenotiService?.id,
  (newId) => {
    if (newId) {
      form.value.slot = null;
      bookingStore.updateCurrentBookingSlot(null);
      getZenotiAvailabilities(selectedDate.value);
    }
  }
);

watch(
  [
    () => bookingStore?.zenotiService?.duration,
    () => bookingStore?.zenotiService?.is_couple_service,
  ],
  ([newDuration, newParticipant]) => {
    if (newDuration && newParticipant) {
      bookingStore.updateCurrentBookingSlot(null);
      getZenotiAvailabilities(selectedDate.value);
    }
  }
);

watch(
  () => props.noMatch,
  (newVal) => {
    showNoMatch.value = false;
    if (newVal) {
      isNotMatched.value = true;
      loading.value = false;
      // setTimeout(() => {
      showNoMatch.value = true;
      // }, 1500);
    } else {
      isNotMatched.value = false;
      showNoMatch.value = false;
    }
  }
);

onMounted(async () => {
  nextTick(() => {
    if (bookingStore.zenotiService?.id) {
      getZenotiAvailabilities(selectedDate.value);
    }
  });

  selectedMonth.value = new Date().toLocaleString(locale, {
    month: "long",
    year: "numeric",
  });

  if (bookingStore?.currentBookingSlot?.slot) {
    form.value.slot = bookingStore.currentBookingSlot.slot;
  }
});
</script>

<style scoped>
.booking-calendar {
  background-color: rgb(var(--brand-neutral-2) / 1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: 1.5rem 1rem;
}
.toggle-button {
  text-align: center;
  background-color: transparent;
  font-size: 1rem;
  font-weight: bold;
  color: rgb(var(--brand-black) / 1);
  border: none;
}
.toggle-button::after {
  width: 0.54906rem;
  height: 0.32531rem;
  display: inline-block;
  background: url(../assets/icons/down.svg) no-repeat;
  background-size: 100%;
  content: "";
  vertical-align: middle;
  margin-left: 0.75rem;
}
.booking-datepicker {
  position: absolute;
  top: 4rem;
  left: 0;
  z-index: 10;
}
.booking-slots-details {
  position: relative;
  overflow: hidden;
  min-height: 300px;
}
.booking-slots-details::before {
  content: "";
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 1px;
  width: 100%;
  background-color: rgba(0 0 0 / 10%);
}
.nav-button {
  position: absolute;
  top: 2rem;
  padding: 0;
  margin: 0;
  background-color: transparent;
  border: none;
}
.nav-button.prev:disabled {
  opacity: 0.3;
  cursor: default;
}
.nav-button.next {
  right: 0;
}
.booking-days {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0 0.62rem;
  padding: 1.5rem 1rem 0;
  text-align: center;
}
.booking-day-header {
  margin-bottom: 1.5rem;
}
.booking-day-date {
  font-size: 0.75rem;
  text-transform: capitalize;
  padding: 0;
  margin: 0;
}
.booking-day-date span {
  display: block;
  font-size: 1.125rem;
  font-weight: bold;
}
.booking-slots {
  display: flex;
  flex-direction: column;
  gap: 0.63rem;
}
.booking-slot {
  margin: 0;
  font-size: 0.875rem;
}
.show-more-button {
  margin: 0;
  font-size: 0.75rem;
  font-weight: bold;
  background-color: transparent;
  padding: 0;
  text-decoration: underline;
  height: auto;
  color: rgb(var(--brand-black) / 1);
  border: none;
}
.message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  text-align: center;

  &.no-match {
    display: none;
  }

  &.active {
    display: block;
  }
}
.next-available-date {
  background-color: rgb(var(--brand-white) / 1);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  padding: 1.5rem;
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 0.875rem;
  gap: 0.5rem;
  max-width: 100%;
  width: 26rem;
}
.next-available-date * {
  flex-shrink: 0;
}
.next-available-date button {
  margin: 0;
  padding: 0;
  font-weight: bold;
  text-transform: capitalize;
  height: auto;
  position: relative;
  background: transparent;
  border: none;
}
.next-available-date button::after {
  width: 0.52525rem;
  height: 0.54169rem;
  display: inline-block;
  background: url(../assets/icons/arrow.svg) no-repeat;
  background-size: 100%;
  content: "";
  vertical-align: middle;
  margin-left: 0.75rem;
}
.next-available-date button::before {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 5rem;
  position: absolute;
  right: -0.35rem;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0 0 0 / 10%);
  content: "";
  margin-top: 0.15rem;
}
@media screen and (min-width: 1000px) {
  /* .booking-slots-details {
    padding: 1.5rem;
  } */
  /* .booking-slots-details::before {
    width: calc(100% - 3rem);
  } */
  .booking-days {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    padding: 1.5rem 2rem 0;
  }
  .next-available-date {
    font-size: 1rem;
  }
  .booking-calendar {
    padding-inline: 1.5rem;
  }
}
</style>
